/**
 * ProClientCard.jsx
 * Route: /pro/clients/:clientId
 *
 * Tab 0 — Details:          view and edit name, phone, email, allergies, preferences, notes
 * Tab 1 — Patch Tests:      list all tests, add new test, update pending result
 * Tab 2 — Appointments:     history from pro_online_bookings joined to pro_sales
 *
 * Uses endpoints:
 *   GET  /api/pro-booking/clients/:id
 *   PUT  /api/pro-booking/clients/:id
 *   GET  /api/patch-tests?client_id=:id
 *   POST /api/patch-tests
 *   PUT  /api/patch-tests/:id
 *   GET  /api/pro-booking/clients/:id/appointments
 *
 * V3 TODO: colour formula records, photo uploads, dedicated notes tab
 * Future: add client_id FK to appointments table to include manual diary entries in history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Button, Tabs, Tab,
  TextField, Grid, Paper, Chip, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import EditIcon           from '@mui/icons-material/Edit';
import SaveIcon           from '@mui/icons-material/Save';
import CancelIcon         from '@mui/icons-material/Cancel';
import AddIcon            from '@mui/icons-material/Add';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import PersonIcon         from '@mui/icons-material/Person';
import ScienceIcon        from '@mui/icons-material/Science';
import EventNoteIcon      from '@mui/icons-material/EventNote';
import api from './axiosConfig';

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const fmtCurrency = (val) => {
  if (val === null || val === undefined) return '—';
  return `£${parseFloat(val).toFixed(2)}`;
};

const today = () => new Date().toISOString().split('T')[0];

// ── Status chip config ─────────────────────────────────────────────────────

const STATUS_CONFIG = {
  confirmed:           { label: 'Confirmed',        bgcolor: '#2e7d32', color: '#fff' },
  completed:           { label: 'Completed',        bgcolor: '#546e7a', color: '#fff' },
  cancelled:           { label: 'Cancelled',        bgcolor: '#c62828', color: '#fff' },
  awaiting_patch_test: { label: 'Awaiting patch test', bgcolor: '#f57c00', color: '#fff' },
  pending_payment:     { label: 'Pending payment',  bgcolor: '#1565c0', color: '#fff' },
  no_show:             { label: 'No show',          bgcolor: '#6a1b9a', color: '#fff' },
};

function StatusChip({ status, size = 'small' }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bgcolor: '#9e9e9e', color: '#fff' };
  return (
    <Chip size={size} label={cfg.label}
      sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
  );
}

// ── Patch test status chip ─────────────────────────────────────────────────

function PatchTestChip({ status, size = 'small' }) {
  if (!status || status === 'none') return (
    <Chip size={size} label="No patch test" sx={{ bgcolor: '#f5f5f5', color: '#757575' }} />
  );
  const config = {
    valid:    { label: 'Valid',    bgcolor: '#2e7d32', color: '#fff', icon: <CheckCircleIcon style={{ fontSize: 14, color: '#fff' }} /> },
    expired:  { label: 'Expired', bgcolor: '#BF360C', color: '#fff', icon: <WarningAmberIcon style={{ fontSize: 14, color: '#fff' }} /> },
    positive: { label: 'Positive (reactive)', bgcolor: '#c62828', color: '#fff', icon: <WarningAmberIcon style={{ fontSize: 14, color: '#fff' }} /> },
    pending:  { label: 'Pending result', bgcolor: '#f57c00', color: '#fff', icon: <WarningAmberIcon style={{ fontSize: 14, color: '#fff' }} /> },
  };
  const cfg = config[status];
  if (!cfg) return null;
  return (
    <Chip size={size} icon={cfg.icon} label={cfg.label}
      sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600 }} />
  );
}

// ── Result chip for individual test rows ──────────────────────────────────

function ResultChip({ result }) {
  const config = {
    negative: { label: 'Negative (clear)', bgcolor: '#2e7d32', color: '#fff' },
    positive: { label: 'Positive (reactive)', bgcolor: '#c62828', color: '#fff' },
    pending:  { label: 'Pending result', bgcolor: '#f57c00', color: '#fff' },
  };
  const cfg = config[result] || { label: result, bgcolor: '#9e9e9e', color: '#fff' };
  return (
    <Chip size="small" label={cfg.label}
      sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
  );
}

// ── Add / Edit patch test dialog ───────────────────────────────────────────

function PatchTestDialog({ open, onClose, onSaved, clientId, existingTest }) {
  const isEdit = !!existingTest;

  const emptyForm = {
    test_date:        today(),
    result:           'pending',
    product_type:     '',
    product_brand:    '',
    product_mix:      '',
    application_site: '',
    administered_by:  '',
    notes:            '',
  };

  const [form, setForm]       = useState(emptyForm);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit && existingTest) {
      setForm({
        test_date:        existingTest.test_date       || today(),
        result:           existingTest.result          || 'pending',
        product_type:     existingTest.product_type    || '',
        product_brand:    existingTest.product_brand   || '',
        product_mix:      existingTest.product_mix     || '',
        application_site: existingTest.application_site || '',
        administered_by:  existingTest.administered_by || '',
        notes:            existingTest.notes           || '',
      });
    } else {
      setForm(emptyForm);
    }
    setError(null);
  }, [open, existingTest]);

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        client_id:        clientId,
        test_date:        form.test_date,
        result:           form.result,
        product_type:     form.product_type   || undefined,
        product_brand:    form.product_brand  || undefined,
        product_mix:      form.product_mix    || undefined,
        application_site: form.application_site || undefined,
        administered_by:  form.administered_by || undefined,
        notes:            form.notes          || undefined,
      };

      if (isEdit) {
        await api.put(`/patch-tests/${existingTest.id}`, payload);
      } else {
        await api.post('/patch-tests', payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save patch test');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Update Patch Test' : 'Record Patch Test'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Test date *" name="test_date" type="date"
              value={form.test_date} onChange={handleField}
              InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Result *</InputLabel>
              <Select name="result" value={form.result} label="Result *"
                onChange={handleField}>
                <MenuItem value="pending">Pending (test done, awaiting 48h)</MenuItem>
                <MenuItem value="negative">Negative (no reaction — clear)</MenuItem>
                <MenuItem value="positive">Positive (reaction — do not proceed)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                Product details (for liability record)
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Product type" name="product_type"
              placeholder="e.g. Permanent colour, Tint"
              value={form.product_type} onChange={handleField} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Product brand" name="product_brand"
              placeholder="e.g. Igora Royal, Wella Koleston"
              value={form.product_brand} onChange={handleField} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Product mix / formula" name="product_mix"
              placeholder="e.g. 5-0 + 6-77 (50/50) with 20vol"
              value={form.product_mix} onChange={handleField} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Application site" name="application_site"
              placeholder="e.g. Behind left ear, Inner elbow"
              value={form.application_site} onChange={handleField} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Administered by" name="administered_by"
              placeholder="Stylist name"
              value={form.administered_by} onChange={handleField} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Notes" name="notes" multiline rows={2}
              placeholder="Any observations, reactions, or additional notes"
              value={form.notes} onChange={handleField} />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Record test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Patch Tests tab ────────────────────────────────────────────────────────

function PatchTestsTab({ clientId }) {
  const [tests, setTests]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [dialogOpen, setDialog]   = useState(false);
  const [editTarget, setEdit]     = useState(null);

  const loadTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/patch-tests', { params: { client_id: clientId } });
      setTests(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load patch tests');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadTests(); }, [loadTests]);

  const handleAdd = () => { setEdit(null); setDialog(true); };
  const handleEdit = (test) => { setEdit(test); setDialog(true); };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Patch test history ({tests.length})
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="small">
          Record patch test
        </Button>
      </Box>

      {tests.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <ScienceIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No patch tests recorded for this client yet.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}
            sx={{ mt: 2 }} size="small">
            Record first patch test
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {tests.map((test) => (
            <Paper key={test.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {fmtDate(test.test_date)}
                  </Typography>
                  <ResultChip result={test.result} />
                  {test.expiry_date && test.result === 'negative' && (
                    <Typography variant="caption" color="text.secondary">
                      Expires {fmtDate(test.expiry_date)}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={test.result === 'pending' ? 'Record result' : 'Edit record'}>
                  <IconButton size="small" onClick={() => handleEdit(test)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={1} sx={{ mt: 0.25 }}>
                {test.product_type && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Product type</Typography>
                    <Typography variant="body2">{test.product_type}</Typography>
                  </Grid>
                )}
                {test.product_brand && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Brand</Typography>
                    <Typography variant="body2">{test.product_brand}</Typography>
                  </Grid>
                )}
                {test.product_mix && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Formula / mix</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {test.product_mix}
                    </Typography>
                  </Grid>
                )}
                {test.application_site && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Application site</Typography>
                    <Typography variant="body2">{test.application_site}</Typography>
                  </Grid>
                )}
                {test.administered_by && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Administered by</Typography>
                    <Typography variant="body2">{test.administered_by}</Typography>
                  </Grid>
                )}
                {test.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{test.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}
        </Stack>
      )}

      <PatchTestDialog
        open={dialogOpen}
        onClose={() => setDialog(false)}
        onSaved={loadTests}
        clientId={clientId}
        existingTest={editTarget}
      />
    </Box>
  );
}

// ── Appointment History tab ────────────────────────────────────────────────

function AppointmentHistoryTab({ clientId }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/pro-booking/clients/${clientId}/appointments`);
      setAppointments(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load appointment history');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Compute lifetime value from completed appointments with a sale total
  const lifetimeValue = appointments
    .filter(a => a.status === 'completed' && a.sale_total !== null)
    .reduce((sum, a) => sum + (a.sale_total || 0), 0);

  const completedCount = appointments.filter(a => a.status === 'completed').length;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box>
      {/* Summary strip */}
      {appointments.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Paper variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total visits
            </Typography>
            <Typography variant="h6" fontWeight={700}>{completedCount}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Lifetime value
            </Typography>
            <Typography variant="h6" fontWeight={700}>{fmtCurrency(lifetimeValue)}</Typography>
          </Paper>
          {appointments.length > 0 && (
            <Paper variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                First visit
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtDate(appointments[appointments.length - 1]?.appointment_date)}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {appointments.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <EventNoteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No appointment history yet for this client.
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
            History shows bookings made through SalonLogic.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Service(s)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Stylist</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map((appt) => (
                <TableRow
                  key={appt.appointment_id || appt.booking_id}
                  sx={{
                    '&:last-child td': { border: 0 },
                    opacity: appt.status === 'cancelled' ? 0.55 : 1,
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {fmtDate(appt.appointment_date)}
                    </Typography>
                    {appt.start_time && (
                      <Typography variant="caption" color="text.secondary">
                        {appt.start_time}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{appt.service_names}</Typography>
                    {appt.discount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Discount: {fmtCurrency(appt.discount)}
                      </Typography>
                    )}
                    {appt.tip > 0 && (
                      <Typography variant="caption" color="success.main" display="block">
                        Tip: {fmtCurrency(appt.tip)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{appt.stylist_name}</Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {appt.sale_total !== null ? fmtCurrency(appt.sale_total) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {appt.payment_method || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={appt.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
        Showing up to 100 most recent bookings made through SalonLogic.
      </Typography>
    </Box>
  );
}

// ── Details tab ────────────────────────────────────────────────────────────

function DetailsTab({ client, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    setForm({
      first_name:  client.first_name  || '',
      last_name:   client.last_name   || '',
      phone:       client.phone       || '',
      email:       client.email       || '',
      allergies:   client.allergies   || '',
      preferences: client.preferences || '',
      notes:       client.notes       || '',
    });
  }, [client]);

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/pro-booking/clients/${client.id}`, form);
      onSaved(res.data);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name:  client.first_name  || '',
      last_name:   client.last_name   || '',
      phone:       client.phone       || '',
      email:       client.email       || '',
      allergies:   client.allergies   || '',
      preferences: client.preferences || '',
      notes:       client.notes       || '',
    });
    setEditing(false);
    setError(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!editing ? (
          <Button variant="outlined" startIcon={<EditIcon />}
            onClick={() => setEditing(true)} size="small">
            Edit details
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<CancelIcon />}
              onClick={handleCancel} disabled={saving} size="small">
              Cancel
            </Button>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave} disabled={saving} size="small">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {editing ? (
            <TextField fullWidth label="First name *" name="first_name"
              value={form.first_name} onChange={handleField} />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">First name</Typography>
              <Typography variant="body1" fontWeight={500}>{client.first_name || '—'}</Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {editing ? (
            <TextField fullWidth label="Last name" name="last_name"
              value={form.last_name} onChange={handleField} />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Last name</Typography>
              <Typography variant="body1" fontWeight={500}>{client.last_name || '—'}</Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {editing ? (
            <TextField fullWidth label="Phone" name="phone"
              value={form.phone} onChange={handleField} />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography variant="body1">
                {client.phone
                  ? <a href={`tel:${client.phone}`} style={{ color: 'inherit' }}>{client.phone}</a>
                  : '—'}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {editing ? (
            <TextField fullWidth label="Email" name="email" type="email"
              value={form.email} onChange={handleField} />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body1">
                {client.email
                  ? <a href={`mailto:${client.email}`} style={{ color: 'inherit' }}>{client.email}</a>
                  : '—'}
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
        </Grid>

        <Grid item xs={12}>
          {editing ? (
            <TextField fullWidth label="Allergies / sensitivities" name="allergies"
              value={form.allergies} onChange={handleField} multiline rows={2}
              helperText="This will show as a warning when booking" />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Allergies / sensitivities</Typography>
              {client.allergies ? (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, borderColor: '#e57373', bgcolor: '#fff5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>
                    ⚠ {client.allergies}
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">None recorded</Typography>
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          {editing ? (
            <TextField fullWidth label="Preferences" name="preferences"
              value={form.preferences} onChange={handleField} multiline rows={2}
              helperText="e.g. preferred products, styling preferences" />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Preferences</Typography>
              <Typography variant="body2" color={client.preferences ? 'text.primary' : 'text.secondary'}>
                {client.preferences || 'None recorded'}
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          {editing ? (
            <TextField fullWidth label="Notes" name="notes"
              value={form.notes} onChange={handleField} multiline rows={3} />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body2" color={client.notes ? 'text.primary' : 'text.secondary'}>
                {client.notes || 'None recorded'}
              </Typography>
            </Box>
          )}
        </Grid>

        {client.created_at && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.disabled">
              Client record created {fmtDate(client.created_at.split('T')[0])}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ── Main ProClientCard ─────────────────────────────────────────────────────

export default function ProClientCard() {
  const { clientId }   = useParams();
  const routerNavigate = useNavigate();

  const [client, setClient]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState(0);

  const loadClient = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/pro-booking/clients/${clientId}`);
      setClient(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadClient(); }, [loadClient]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
      <Button sx={{ mt: 2 }} onClick={() => routerNavigate(-1)} startIcon={<ArrowBackIcon />}>
        Back
      </Button>
    </Box>
  );

  if (!client) return null;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => routerNavigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <PersonIcon sx={{ color: 'text.secondary' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
            {client.full_name}
          </Typography>
          {client.phone && (
            <Typography variant="body2" color="text.secondary">{client.phone}</Typography>
          )}
        </Box>
        <PatchTestChip status={client.patch_test?.status} size="medium" />
      </Box>

      {/* Allergy banner — always visible if set */}
      {client.allergies && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderColor: '#e57373', bgcolor: '#fff5f5', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 700 }}>
            ⚠ Allergies / sensitivities: {client.allergies}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Tab label="Details" />
          <Tab label="Patch Tests" />
          <Tab label="Appointments" icon={<EventNoteIcon sx={{ fontSize: 16 }} />} iconPosition="end" />
        </Tabs>

        <Box sx={{ p: 2.5 }}>
          {tab === 0 && (
            <DetailsTab
              client={client}
              onSaved={(updated) => setClient(prev => ({ ...prev, ...updated }))}
            />
          )}
          {tab === 1 && (
            <PatchTestsTab clientId={parseInt(clientId)} />
          )}
          {tab === 2 && (
            <AppointmentHistoryTab clientId={parseInt(clientId)} />
          )}
        </Box>
      </Paper>

    </Box>
  );
}